import DialogTitle from "@mui/material/DialogTitle";
import Translate from "../../../utils/Translate";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import ButtonWithSpinner from "../../ButtonWithSpinner";
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../../../utils/Api";
import Preloader from "../../Preloader";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { IconAndLabelColumnHeader } from "../../../utils/dataGrid";
import { Badge, Build, CalendarToday, CoPresent, Person, School } from "@mui/icons-material";
import { formatDate } from "../../../utils/dateTimeFormat";
import { dataGridLang } from "../../MemToolBar";
import { useSnackbar } from "notistack";

export default function ExportBrokerDataDialog({ actorLicenseIds: actorLicenseIdsProp }) {
  if (!actorLicenseIdsProp || actorLicenseIdsProp.length === 0) throw new Error("Missing prop");

  const [isLoading, setIsLoading] = useState(true);
  const [actorLicenseIds] = useState(actorLicenseIdsProp);
  const [exportData, setExportData] = useState([]);
  const [exportTargets, setExportTargets] = useState([]);
  const [selectedExportTarget, setSelectedExportTarget] = useState(null);

  const api = useApi();
  const localizedTextsMap = dataGridLang();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    async function init() {
      const targetsResponse = await api.fetchWithOverride(
        `${import.meta.env.VITE_MAIN_URL}permits/broker/export-targets`,
        false,
        "GET",
      );

      if (targetsResponse.isSuccessful) {
        setExportTargets(targetsResponse.targets);
      }

      const exportDataParams = new URLSearchParams();
      actorLicenseIds.forEach((id) => exportDataParams.append("actorLicenseIds", id));

      const dataResponse = await api.fetchWithOverride(
        `${import.meta.env.VITE_MAIN_URL}permits/broker/export-data?${exportDataParams}`,
        false,
        "GET",
      );

      if (dataResponse.isSuccessful) {
        setExportData([...dataResponse.data]);
      }

      setIsLoading(false);
    }

    if (actorLicenseIds) {
      init();
    }
  }, [api, actorLicenseIds]);

  const handleExport = useCallback(async () => {
    if (!selectedExportTarget || !actorLicenseIds || actorLicenseIds.length === 0) return;

    const response = await api.fetchWithOverride(
      `${import.meta.env.VITE_MAIN_URL}permits/broker/export`,
      { targetDataId: selectedExportTarget, actorLicenseIds },
      "POST",
    );

    if (response.isSuccessful) {
      const successMessage = enqueueSnackbar(Translate.get("PermitBrokerExportSuccessMessage"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
      window.history.back();
    }
  }, [selectedExportTarget, actorLicenseIds, enqueueSnackbar]);

  const columns = useMemo(
    () => [
      {
        field: "actorName",
        headerName: Translate.get("ActorName"),
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={Person} label={Translate.get("ActorName")} />,
        valueGetter: (value, row, column, apiRef) => row.actor.actorName || "",
      },
      {
        field: "licenseGroupDescription",
        headerName: Translate.get("LicenceType"),
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={School} label={Translate.get("LicenceType")} />,
        valueGetter: (value, row, column, apiRef) => row.actorLicense.licenseGroupDescription || "",
      },
      {
        field: "customx",
        headerName: Translate.get("Competences"),
        renderHeader: () => <IconAndLabelColumnHeader icon={Build} label={Translate.get("Competences")} />,
        renderCell: (params) => {
          const value = [params.row.actorLicense.licenseGroupPractice, params.row.actorLicense.licenseGroupTheory]
            .filter((s) => !!s)
            .join(", ");

          return (
            <div title={value} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {value}
            </div>
          );
        },
        valueGetter: (value, row, column, apiRef) => row.actorLicense.licenseGroupPractice || "",
        sortComparator: (v1, v2) => v1.localeCompare(v2),
        flex: 1,
      },
      {
        field: "validFrom",
        flex: 1,
        headerName: Translate.get("ValidTime"),
        renderHeader: () => <IconAndLabelColumnHeader icon={CalendarToday} label={Translate.get("ValidTime")} />,
        valueGetter: (value, row, column, apiRef) =>
          `${row.actorLicense.validFrom ? formatDate(row.actorLicense.validFrom) : ""} - ${row.actorLicense.validTo ? formatDate(row.actorLicense.validTo) : ""}`,
      },
      {
        field: "instructorName",
        headerName: Translate.get("Instructor"),
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={CoPresent} label={Translate.get("Instructor")} />,
        valueGetter: (value, row, column, apiRef) => row.actorLicense.instructorName || "",
      },
    ],
    [],
  );

  const listViewColDef = useMemo(
    () => ({
      field: "listColumn",
      renderCell: (params) => {
        const license = params.row.actorLicense;
        const competences = [license.licenseGroupPractice, license.licenseGroupTheory].filter((s) => !!s).join(", ");
        const validTime = `${license.validFrom ? formatDate(license.validFrom) : ""}${
          license.validTo ? ` - ${formatDate(license.validTo)}` : ""
        }`;

        const fields = [
          { icon: <Badge />, value: license.licenseGroupDescription },
          { icon: <Build />, value: competences },
          { icon: <CalendarToday />, value: validTime },
          { icon: <CoPresent />, value: license.instructorName },
        ];

        return (
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              marginY: "8px",
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Stack spacing={2} sx={{ padding: 2 }}>
              {fields.map(({ icon, value }, index) => (
                <Stack key={index} direction="row" alignItems="center" spacing={2}>
                  {icon}
                  <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{value}</Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        );
      },
    }),
    [theme],
  );

  return (
    <>
      {isLoading && <Preloader />}
      <DialogTitle>{Translate.get("ExportBrokerDataTitle")}</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={4} mt={1}>
          <FormControl fullWidth required>
            <InputLabel id="export-target-label" required>
              {Translate.get("ExportBrokerDataTargetCompany")}
            </InputLabel>
            <Select
              required
              label={Translate.get("ExportBrokerDataTargetCompany")}
              labelId="export-target-label"
              value={selectedExportTarget ?? ""}
              onChange={(event) => setSelectedExportTarget(event.target.value)}
            >
              {exportTargets.map((option) => (
                <MenuItem key={option.dataId} value={option.dataId}>
                  {`${option.companyName} (${option.dataId})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="column" spacing={2}>
            <h4>{`${Translate.get("ExportBrokerDataLicenses")}:`}</h4>
            <DataGridPremium
              rows={exportData}
              columns={columns}
              disableRowSelectionOnClick
              getRowId={(row) => row.actorLicense.actorLicenseId}
              initialState={{
                sorting: {
                  sortModel: [{ field: "validFrom", sort: "desc" }],
                },
              }}
              localeText={localizedTextsMap}
              disableColumnMenu
              getRowHeight={() => (isSmallScreen ? "auto" : 65)}
              listView={isSmallScreen}
              listViewColumn={isSmallScreen ? listViewColDef : undefined}
              hideFooter
              sx={isSmallScreen ? { "& .MuiDataGrid-cell": { borderTop: "none" } } : undefined}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => window.history.back()}>{Translate.get("Cancel")}</Button>
        <ButtonWithSpinner variant="contained" disabled={!selectedExportTarget} onClick={handleExport}>
          {Translate.get("Export")}
        </ButtonWithSpinner>
      </DialogActions>
    </>
  );
}
